import { create } from 'zustand'
import type { LoraInput, PAGConfig, FreeUConfig, GenerationRequest } from './types'

interface GenerationFormState {
  // Form fields
  readonly checkpoint: string
  readonly positivePrompt: string
  readonly negativePrompt: string
  readonly width: number
  readonly height: number
  readonly samplerName: string
  readonly scheduler: string
  readonly steps: number
  readonly cfgScale: number
  readonly seed: number
  readonly loras: readonly LoraInput[]

  // PAG
  readonly pagEnabled: boolean
  readonly pagScale: number

  // FreeU
  readonly freeuEnabled: boolean
  readonly freeuB1: number
  readonly freeuB2: number
  readonly freeuS1: number
  readonly freeuS2: number

  // Upscale
  readonly upscaleFactor: number

  // UI state
  readonly isGenerating: boolean
  readonly activeGenerationId: string | null

  // Actions
  readonly setCheckpoint: (value: string) => void
  readonly setPositivePrompt: (value: string) => void
  readonly setNegativePrompt: (value: string) => void
  readonly setWidth: (value: number) => void
  readonly setHeight: (value: number) => void
  readonly setSamplerName: (value: string) => void
  readonly setScheduler: (value: string) => void
  readonly setSteps: (value: number) => void
  readonly setCfgScale: (value: number) => void
  readonly setSeed: (value: number) => void
  readonly addLora: (lora: LoraInput) => void
  readonly removeLora: (index: number) => void
  readonly updateLoraWeight: (index: number, weight: number) => void

  // PAG actions
  readonly setPagEnabled: (value: boolean) => void
  readonly setPagScale: (value: number) => void

  // FreeU actions
  readonly setFreeuEnabled: (value: boolean) => void
  readonly setFreeuB1: (value: number) => void
  readonly setFreeuB2: (value: number) => void
  readonly setFreeuS1: (value: number) => void
  readonly setFreeuS2: (value: number) => void

  // Upscale actions
  readonly setUpscaleFactor: (value: number) => void

  readonly setIsGenerating: (value: boolean) => void
  readonly setActiveGenerationId: (id: string | null) => void
  readonly applyDefaults: (defaults: {
    checkpoint?: string | null
    negativePrompt?: string
    samplerName?: string
    scheduler?: string
    steps?: number
    cfgScale?: number
    width?: number
    height?: number
    pagEnabled?: boolean
    pagScale?: number
    freeuEnabled?: boolean
    freeuB1?: number
    freeuB2?: number
    freeuS1?: number
    freeuS2?: number
  }) => void
  readonly toRequest: () => GenerationRequest
  readonly reset: () => void
}

const DEFAULT_STATE = {
  checkpoint: '',
  positivePrompt: '',
  negativePrompt: '',
  width: 512,
  height: 512,
  samplerName: 'euler',
  scheduler: 'normal',
  steps: 20,
  cfgScale: 7.0,
  seed: -1,
  loras: [] as readonly LoraInput[],
  pagEnabled: false,
  pagScale: 3.0,
  freeuEnabled: false,
  freeuB1: 1.1,
  freeuB2: 1.2,
  freeuS1: 0.9,
  freeuS2: 0.2,
  upscaleFactor: 1.0,
  isGenerating: false,
  activeGenerationId: null,
} as const

export const useGenerationFormStore = create<GenerationFormState>()((set, get) => ({
  ...DEFAULT_STATE,

  setCheckpoint: (value) => { set({ checkpoint: value }) },
  setPositivePrompt: (value) => { set({ positivePrompt: value }) },
  setNegativePrompt: (value) => { set({ negativePrompt: value }) },
  setWidth: (value) => { set({ width: value }) },
  setHeight: (value) => { set({ height: value }) },
  setSamplerName: (value) => { set({ samplerName: value }) },
  setScheduler: (value) => { set({ scheduler: value }) },
  setSteps: (value) => { set({ steps: value }) },
  setCfgScale: (value) => { set({ cfgScale: value }) },
  setSeed: (value) => { set({ seed: value }) },

  addLora: (lora) => {
    set((state) => ({ loras: [...state.loras, lora] }))
  },
  removeLora: (index) => {
    set((state) => ({
      loras: state.loras.filter((_, i) => i !== index),
    }))
  },
  updateLoraWeight: (index, weight) => {
    set((state) => ({
      loras: state.loras.map((lora, i) =>
        i === index ? { ...lora, weight } : lora,
      ),
    }))
  },

  // PAG
  setPagEnabled: (value) => { set({ pagEnabled: value }) },
  setPagScale: (value) => { set({ pagScale: value }) },

  // FreeU
  setFreeuEnabled: (value) => { set({ freeuEnabled: value }) },
  setFreeuB1: (value) => { set({ freeuB1: value }) },
  setFreeuB2: (value) => { set({ freeuB2: value }) },
  setFreeuS1: (value) => { set({ freeuS1: value }) },
  setFreeuS2: (value) => { set({ freeuS2: value }) },

  // Upscale
  setUpscaleFactor: (value) => { set({ upscaleFactor: value }) },

  setIsGenerating: (value) => { set({ isGenerating: value }) },
  setActiveGenerationId: (id) => { set({ activeGenerationId: id }) },

  toRequest: (): GenerationRequest => {
    const state = get()
    return {
      checkpoint: state.checkpoint,
      positive_prompt: state.positivePrompt,
      negative_prompt: state.negativePrompt,
      width: state.width,
      height: state.height,
      sampler_name: state.samplerName,
      scheduler: state.scheduler,
      steps: state.steps,
      cfg_scale: state.cfgScale,
      seed: state.seed,
      loras: [...state.loras],
      pag: state.pagEnabled ? { scale: state.pagScale } : null,
      freeu: state.freeuEnabled
        ? { b1: state.freeuB1, b2: state.freeuB2, s1: state.freeuS1, s2: state.freeuS2 }
        : null,
      upscale_factor: state.upscaleFactor,
    }
  },

  applyDefaults: (defaults) => {
    const patch: Record<string, string | number | boolean> = {}
    if (defaults.checkpoint) patch['checkpoint'] = defaults.checkpoint
    if (defaults.negativePrompt) patch['negativePrompt'] = defaults.negativePrompt
    if (defaults.samplerName) patch['samplerName'] = defaults.samplerName
    if (defaults.scheduler) patch['scheduler'] = defaults.scheduler
    if (defaults.steps && defaults.steps > 0) patch['steps'] = defaults.steps
    if (defaults.cfgScale && defaults.cfgScale > 0) patch['cfgScale'] = defaults.cfgScale
    if (defaults.width && defaults.width > 0) patch['width'] = defaults.width
    if (defaults.height && defaults.height > 0) patch['height'] = defaults.height
    if (defaults.pagEnabled !== undefined) patch['pagEnabled'] = defaults.pagEnabled
    if (defaults.pagScale !== undefined) patch['pagScale'] = defaults.pagScale
    if (defaults.freeuEnabled !== undefined) patch['freeuEnabled'] = defaults.freeuEnabled
    if (defaults.freeuB1 !== undefined) patch['freeuB1'] = defaults.freeuB1
    if (defaults.freeuB2 !== undefined) patch['freeuB2'] = defaults.freeuB2
    if (defaults.freeuS1 !== undefined) patch['freeuS1'] = defaults.freeuS1
    if (defaults.freeuS2 !== undefined) patch['freeuS2'] = defaults.freeuS2
    set(patch as Partial<GenerationFormState>)
  },

  reset: () => { set({ ...DEFAULT_STATE }) },
}))
